import { app } from '@microsoft/teams-js';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Logger } from '../src/packages/logger/src/Logger';
import { App } from './App';
import './localization/i18n';
import './style.scss';

// Declare global watchdog cancellation function (set in public/index.html)
declare global {
  interface Window {
    __cancelStartupWatchdog?: () => void;
  }
}

// Log app version and initialization
console.log(`Cisco Call MS Teams Client - Version ${process.env.APP_VERSION}`);
Logger.debug(`App Version: ${process.env.APP_VERSION}`);

// Store version in sessionStorage to detect updates
const currentVersion = process.env.APP_VERSION;
const previousVersion = sessionStorage.getItem('app_version');

if (previousVersion && previousVersion !== currentVersion) {
  console.log(`Updated from version ${previousVersion} to ${currentVersion}`);
} else if (!previousVersion) {
  console.log(`First launch - Version ${currentVersion}`);
}

sessionStorage.setItem('app_version', currentVersion || '');

/**
 * Log startup performance metric
 * Tracks app initialization timing for diagnostics (BEMS01947865, BEMS01948131)
 */
function logStartupMetric(eventName: string, additionalData?: Record<string, unknown>): void {
  try {
    const metrics = JSON.parse(localStorage.getItem('cisco_call_startup_metrics') || '{}');
    const entry = {
      timestamp: new Date().toISOString(),
      performanceNow: Math.round(performance.now()),
      ...additionalData,
    };
    metrics[eventName] = entry;
    metrics.lastEvent = eventName;
    localStorage.setItem('cisco_call_startup_metrics', JSON.stringify(metrics));
  } catch (e) {
    // localStorage may not be available - use console.warn as Logger may not support warn level
    console.warn('Failed to log startup metric:', e);
  }
}

// Log script execution start
logStartupMetric('scriptStart');

app.initialize().then(async () => {
  logStartupMetric('teamsInitialized');
  app.notifySuccess();
  logStartupMetric('notifySuccessCalled');

  // Defense-in-depth: use the SDK's authoritative clientType to unregister
  // any service worker that the UA heuristic in index.html failed to block.
  // BEMS01964778: new Teams (WebView2) was not caught by the old Electron-only UA check.
  try {
    const context = await app.getContext();
    const clientType = context?.app?.host?.clientType;
    if (clientType === 'desktop' && 'serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length > 0) {
        Logger.info(`[SW] Desktop client confirmed via SDK (clientType=${clientType}), ` +
                     `unregistering ${registrations.length} service worker(s)`);
        logStartupMetric('swUnregisteredBySDK', { clientType, count: registrations.length });
        await Promise.all(registrations.map(r => r.unregister()));
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        }
      }
    }
  } catch (ctxError) {
    console.warn('[SW] Could not verify clientType for SW safety check:', ctxError);
  }
}).catch((error) => {
  logStartupMetric('teamsInitFailed', { error: String(error) });
  Logger.error(error);
});

const rootElement = document.getElementById('root') as HTMLElement;

// Signal to the watchdog that the JS bundle loaded and the app is initializing.
// This prevents the watchdog from triggering a destructive reload while Webex
// device registration or OAuth flows are still in progress.
rootElement.setAttribute('data-app-state', 'initializing');
logStartupMetric('appInitializing');

const root = createRoot(rootElement);

root.render(
  <StrictMode>
    <App />
  </StrictMode>
);

rootElement.setAttribute('data-app-state', 'rendered');

if (typeof window.__cancelStartupWatchdog === 'function') {
  window.__cancelStartupWatchdog();
  logStartupMetric('watchdogCancelled');
}

logStartupMetric('reactRenderCalled');
